import { Dispatch, SetStateAction, useEffect } from "react"
import { IDeckHandCard } from "@/store/apis/deckAPI"
import {
  IGameplayStatePlayer,
  IGameplayStateResponse
} from "@/store/apis/gameplayAPI"
import { ICardFront } from "@/ui/Card"
import {
  ICardItem,
  ICardOwner,
  mockCardData,
  mockCardGoldData,
  makeCardItem
} from ".."
import { IInitGuards } from "./useCardItems"

interface IUseCardInitParams {
  // From useGame()
  playerCardsData: IDeckHandCard[] | undefined
  opponentCardsData: IDeckHandCard[] | undefined
  isPlayerCardsLoading: boolean
  gameState: IGameplayStateResponse | undefined
  isGameStateLoading: boolean
  isMatchActive: boolean
  player: IGameplayStatePlayer | null | undefined
  opponent: IGameplayStatePlayer | null | undefined

  // From useCardItems()
  addTo: (owner: ICardOwner, data?: ICardFront, count?: number) => void
  setItems: Dispatch<SetStateAction<ICardItem[]>>
  initGuards: IInitGuards
  checkFullInit: () => void
}

export const useCardInit = ({
  playerCardsData,
  opponentCardsData,
  isPlayerCardsLoading,
  gameState,
  isGameStateLoading,
  isMatchActive,
  player,
  opponent,
  addTo,
  setItems,
  initGuards,
  checkFullInit
}: IUseCardInitParams): void => {
  const {
    isInitPlayerCards,
    isInitDeckCards,
    isInitGoldCards,
    isInitOpponentCards,
    isInitOpponentViewCards,
    isInitCurrentCard,
    isInitPrevCard
  } = initGuards

  // Effect 1 — Init player cards
  useEffect(() => {
    if (isInitPlayerCards.current) return
    if (isPlayerCardsLoading || !playerCardsData) return

    isInitPlayerCards.current = true
    checkFullInit()

    const newCards = playerCardsData.map((card) =>
      makeCardItem({
        owner: "user",
        order: 0,
        data: {
          ...card.card,
          ownerId: player?.player
        }
      })
    )

    setItems((prev) => {
      const maxUserOrder = prev
        .filter((i) => i.owner === "user")
        .reduce((max, i) => Math.max(max, i.order), 0)

      return [
        ...prev,
        ...newCards.map((c, index) => ({
          ...c,
          order: maxUserOrder + index + 1
        }))
      ]
    })
  }, [isPlayerCardsLoading, playerCardsData])

  // Effect 2 — Init deck cards
  useEffect(() => {
    if (isGameStateLoading || isInitDeckCards.current) return

    isInitDeckCards.current = true
    checkFullInit()

    if (!gameState) return

    addTo("deck", mockCardData, gameState.deckSize)
  }, [isGameStateLoading, gameState])

  // Effect 3 — Init gold cards
  useEffect(() => {
    if (isInitGoldCards.current) return

    if (isMatchActive) {
      isInitGoldCards.current = true
      addTo("gold", mockCardGoldData, 1)
    }
  }, [isMatchActive])

  // Effect 4 — Init opponent cards
  useEffect(() => {
    if (isInitOpponentCards.current || !opponent?.player) return

    if (isMatchActive) {
      isInitOpponentCards.current = true
      checkFullInit()
      addTo("opponent", { ...mockCardData, ownerId: opponent.player }, 5)
    }
  }, [isMatchActive, opponent?.player])

  // Effect 5 — Init opponent view cards
  useEffect(() => {
    if (isInitOpponentViewCards.current || !opponent) return

    if (isPlayerCardsLoading || !opponentCardsData) return

    isInitOpponentViewCards.current = true

    const newCards = opponentCardsData.map((card) =>
      makeCardItem({
        owner: "opponent-view",
        order: 0,
        data: { ...card.card, ownerId: opponent?.player }
      })
    )

    setItems((prev) => {
      const maxOrder = prev
        .filter((i) => i.owner === "opponent-view")
        .reduce((max, i) => Math.max(max, i.order), 0)

      return [
        ...prev,
        ...newCards.map((c, index) => ({
          ...c,
          order: maxOrder + index + 1
        }))
      ]
    })
  }, [isPlayerCardsLoading, opponentCardsData, isMatchActive, opponent])

  // Effect 6 — Init current card
  useEffect(() => {
    if (isGameStateLoading || !gameState || isInitCurrentCard.current) return

    isInitCurrentCard.current = true

    if (!gameState.currentCard) return

    addTo("active", {
      ...gameState.currentCard,
      ownerId: gameState.currentCardPlayedBy
    })
  }, [isGameStateLoading, gameState])

  // Effect 7 — Init prev card
  useEffect(() => {
    if (isGameStateLoading || !gameState || isInitPrevCard.current) return

    isInitPrevCard.current = true

    if (!gameState.prevCard) return

    addTo("archive", {
      ...gameState.prevCard,
      ownerId: gameState.prevCardPlayedBy
    })
  }, [isGameStateLoading, gameState])
}
